import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../../utils/axiosConfig';
import { normalizarTexto } from '../../../utils/texto';
import { Cargando, DIAS_CORTOS, botonAzul, botonClaro, display, horaAmPm, horas, iso, panel } from '../ui';

const NOMBRES_DIAS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const campo = 'h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-400/50';
const boton = 'h-9 px-3 rounded-lg text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 disabled:opacity-40';
const TRAMO = { entrada: '08:00', salida: '17:00' };
const VACIO = { nombre: '', jornada: [TRAMO, TRAMO, TRAMO, TRAMO, TRAMO, null, null], tolerancia_min: 0, descuento_almuerzo_min: 60, extra_minimo_min: 30 };
const hoyISO = () => iso(new Date());

const errorDe = (e, texto) => e.response?.data?.error || texto;
const aMin = (hhmm) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; };

// Escala del dibujo de la semana: de 5 a. m. a 9 p. m.
const INICIO = 5 * 60;
const FIN = 21 * 60;
const pct = (min) => `${((min - INICIO) / (FIN - INICIO)) * 100}%`;

// La semana del horario: una barra por día con su entrada y salida
const SemanaHorario = ({ jornada }) => (
    <div>
        <div className="space-y-1.5">
            {NOMBRES_DIAS.map((nombre, i) => {
                const t = jornada[i];
                return (
                    <div key={nombre} className="flex items-center gap-3">
                        <span className="w-9 text-xs text-slate-500">{DIAS_CORTOS[i]}</span>
                        <div className="relative flex-1 h-6 rounded-md bg-slate-50">
                            {[6, 9, 12, 15, 18].map((h) => (
                                <span key={h} className="absolute top-0 bottom-0 w-px bg-slate-200/70" style={{ left: pct(h * 60) }} aria-hidden />
                            ))}
                            {t ? (
                                <div className="absolute top-0.5 bottom-0.5 rounded bg-[#1F4E8C] flex items-center px-2 overflow-hidden"
                                    style={{ left: pct(aMin(t.entrada)), width: `calc(${pct(aMin(t.salida))} - ${pct(aMin(t.entrada))})` }}>
                                    <span className="text-[11px] font-medium text-white whitespace-nowrap">
                                        {horaAmPm(t.entrada)} a {horaAmPm(t.salida)}
                                    </span>
                                </div>
                            ) : (
                                <span className="absolute inset-0 flex items-center px-2 text-[11px] text-slate-400 rayado rounded-md">No se trabaja</span>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
        <div className="relative ml-12 h-4 mt-1" aria-hidden>
            {[6, 9, 12, 15, 18].map((h) => (
                <span key={h} className="absolute -translate-x-1/2 text-[10px] text-slate-400 whitespace-nowrap" style={{ left: pct(h * 60) }}>
                    {h % 12 || 12} {h < 12 ? 'a. m.' : 'p. m.'}
                </span>
            ))}
        </div>
    </div>
);

const horasSemana = (h) => h.jornada.reduce((total, t) => (t ? total + Math.max(aMin(t.salida) - aMin(t.entrada) - (h.descuento_almuerzo_min || 0), 0) / 60 : total), 0);

// Formulario de un horario (nuevo o existente): entrada y salida por día
const FormHorario = ({ inicial, onGuardar, onCancelar }) => {
    const [h, setH] = useState(inicial);
    const [guardando, setGuardando] = useState(false);
    const cambiar = (k, v) => setH((x) => ({ ...x, [k]: v }));
    const cambiarDia = (i, tramo) => setH((x) => ({ ...x, jornada: x.jornada.map((t, j) => (j === i ? tramo : t)) }));

    const guardar = async (e) => {
        e.preventDefault();
        setGuardando(true);
        try {
            if (h.id) await axiosInstance.put(`/api/asistencia/horarios/${h.id}`, h);
            else await axiosInstance.post('/api/asistencia/horarios', h);
            onGuardar();
        } catch (err) {
            toast.error(errorDe(err, 'No se pudo guardar el horario'));
            setGuardando(false);
        }
    };

    return (
        <form onSubmit={guardar} className="p-6 bg-white rounded-2xl border-2 border-cyan-200 space-y-5">
            <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1">
                    <span className="text-xs text-slate-500">Nombre</span>
                    <input value={h.nombre} onChange={(e) => cambiar('nombre', e.target.value)} className={`${campo} w-48`} required />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-xs text-slate-500">Tolerancia (min)</span>
                    <input type="number" min="0" max="240" value={h.tolerancia_min} onChange={(e) => cambiar('tolerancia_min', e.target.value)} className={`${campo} w-24`} />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-xs text-slate-500">Almuerzo que se descuenta (min)</span>
                    <input type="number" min="0" max="240" value={h.descuento_almuerzo_min} onChange={(e) => cambiar('descuento_almuerzo_min', e.target.value)} className={`${campo} w-24`} />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-xs text-slate-500">Hora extra desde (min después de la salida)</span>
                    <input type="number" min="0" max="240" value={h.extra_minimo_min} onChange={(e) => cambiar('extra_minimo_min', e.target.value)} className={`${campo} w-24`} />
                </label>
            </div>
            <div className="space-y-2">
                {NOMBRES_DIAS.map((nombre, i) => {
                    const tramo = h.jornada[i];
                    return (
                        <div key={nombre} className="flex flex-wrap items-center gap-3">
                            <label className="w-32 inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                                <input
                                    type="checkbox" checked={!!tramo}
                                    onChange={(e) => cambiarDia(i, e.target.checked ? { ...TRAMO } : null)}
                                    className="rounded border-slate-300 text-cyan-500 focus:ring-cyan-400/50"
                                />
                                {nombre}
                            </label>
                            {tramo ? (
                                <>
                                    <input type="time" value={tramo.entrada} onChange={(e) => cambiarDia(i, { ...tramo, entrada: e.target.value })} className={campo} aria-label={`Entrada del ${nombre}`} required />
                                    <span className="text-sm text-slate-400">a</span>
                                    <input type="time" value={tramo.salida} onChange={(e) => cambiarDia(i, { ...tramo, salida: e.target.value })} className={campo} aria-label={`Salida del ${nombre}`} required />
                                </>
                            ) : (
                                <span className="text-sm text-slate-400">No se trabaja</span>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="flex justify-end gap-2">
                <button type="button" onClick={onCancelar} className={`${boton} text-slate-600 hover:bg-slate-100`}>Cancelar</button>
                <button type="submit" disabled={guardando} className={`${boton} bg-sura-blue text-white hover:bg-sura-blue-dark`}>
                    {guardando ? 'Guardando...' : 'Guardar'}
                </button>
            </div>
        </form>
    );
};

const Asignar = ({ horarios, onCambio }) => {
    const [empleados, setEmpleados] = useState(null);
    const [horarioId, setHorarioId] = useState('');
    const [desde, setDesde] = useState(hoyISO());
    const [texto, setTexto] = useState('');
    const [elegidos, setElegidos] = useState(new Set());
    const [guardando, setGuardando] = useState(false);

    const cargar = useCallback(() => {
        axiosInstance.get('/api/asistencia/empleados').then(({ data }) => setEmpleados(data.empleados)).catch(() => setEmpleados([]));
    }, []);
    useEffect(cargar, [cargar]);

    const visibles = useMemo(() => {
        const q = normalizarTexto(texto.trim());
        return (empleados || []).filter((e) => !q || e.cedula.includes(q)
            || normalizarTexto(`${e.nombre} ${e.departamento || ''} ${e.sede || ''}`).includes(q));
    }, [empleados, texto]);

    const alternar = (id) => setElegidos((s) => {
        const n = new Set(s);
        if (n.has(id)) n.delete(id); else n.add(id);
        return n;
    });
    const todosVisibles = visibles.length > 0 && visibles.every((e) => elegidos.has(e.id));
    const alternarVisibles = () => setElegidos((s) => {
        const n = new Set(s);
        visibles.forEach((e) => (todosVisibles ? n.delete(e.id) : n.add(e.id)));
        return n;
    });

    const asignar = async () => {
        setGuardando(true);
        try {
            await axiosInstance.post('/api/asistencia/asignaciones', { horario_id: Number(horarioId), desde, empleado_ids: [...elegidos] });
            toast.success(`Horario asignado a ${elegidos.size}`);
            setElegidos(new Set());
            cargar();
            onCambio();
        } catch (e) {
            toast.error(errorDe(e, 'No se pudo asignar'));
        } finally {
            setGuardando(false);
        }
    };

    return (
        <section className="space-y-3">
            <div>
                <h2 className={`${display} text-xl font-semibold text-slate-900`}>Asignar horario</h2>
                <p className="text-sm text-slate-500 mt-0.5">Elige el horario, desde cuándo, y marca a las personas. Lo que tenían antes se cierra el día anterior.</p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1">
                    <span className="text-xs text-slate-500">Horario</span>
                    <select value={horarioId} onChange={(e) => setHorarioId(e.target.value)} className={campo}>
                        <option value="">Elige uno</option>
                        {horarios.filter((h) => h.activo).map((h) => <option key={h.id} value={h.id}>{h.nombre}</option>)}
                    </select>
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-xs text-slate-500">Desde</span>
                    <input type="date" value={desde} onChange={(e) => e.target.value && setDesde(e.target.value)} className={campo} />
                </label>
                <label className="flex flex-col gap-1 flex-1 min-w-[12rem]">
                    <span className="text-xs text-slate-500">Buscar</span>
                    <div className="relative">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input type="search" value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Nombre, cédula, departamento o sede" className={`${campo} w-full pl-9`} />
                    </div>
                </label>
                <button
                    onClick={asignar} disabled={!horarioId || !elegidos.size || guardando}
                    className={botonAzul}
                >
                    {guardando ? 'Asignando...' : elegidos.size ? `Asignar a ${elegidos.size}` : 'Asignar'}
                </button>
            </div>
            <div className={`${panel} max-h-96 overflow-y-auto scroll-fino`}>
                {!empleados ? (
                    <div className="flex justify-center p-8"><Loader2 size={22} className="animate-spin text-slate-400" /></div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-white">
                            <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                                <th className="px-4 py-2.5 w-10">
                                    <input type="checkbox" checked={todosVisibles} onChange={alternarVisibles} aria-label="Elegir todos los de la lista" className="rounded border-slate-300 text-cyan-500 focus:ring-cyan-400/50" />
                                </th>
                                <th className="px-2 py-2.5 font-medium">Persona</th>
                                <th className="px-2 py-2.5 font-medium">Departamento</th>
                                <th className="px-4 py-2.5 font-medium">Horario de hoy</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {visibles.map((e) => (
                                <tr key={e.id} onClick={() => alternar(e.id)} className="cursor-pointer hover:bg-slate-50">
                                    <td className="px-4 py-2">
                                        <input type="checkbox" checked={elegidos.has(e.id)} onChange={() => alternar(e.id)} onClick={(ev) => ev.stopPropagation()} aria-label={e.nombre} className="rounded border-slate-300 text-cyan-500 focus:ring-cyan-400/50" />
                                    </td>
                                    <td className="px-2 py-2 text-slate-900">{e.nombre}</td>
                                    <td className="px-2 py-2 text-slate-500">{e.departamento}{e.sede && <span className="text-slate-400"> ({e.sede})</span>}</td>
                                    <td className="px-4 py-2 text-slate-600">{e.horario}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </section>
    );
};

const HorariosVista = () => {
    const [horarios, setHorarios] = useState(null);
    const [editando, setEditando] = useState(null);
    const cargar = useCallback(() => {
        axiosInstance.get('/api/asistencia/horarios').then(({ data }) => setHorarios(data.horarios)).catch(() => setHorarios([]));
    }, []);
    useEffect(cargar, [cargar]);

    return (
        <div className="space-y-8">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className={`${display} text-3xl font-bold text-slate-900`}>Horarios</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Contra el horario se miden los retardos, las salidas antes de hora y las horas extra.
                    </p>
                </div>
                {!editando && (
                    <button type="button" onClick={() => setEditando({ ...VACIO })} className={botonClaro}>
                        <Plus size={15} /> Nuevo horario
                    </button>
                )}
            </header>

            {!horarios ? <Cargando /> : (
                <div className="space-y-4">
                    {editando && !editando.id && (
                        <FormHorario inicial={editando} onCancelar={() => setEditando(null)} onGuardar={() => { setEditando(null); cargar(); }} />
                    )}
                    {horarios.map((h) => (editando?.id === h.id ? (
                        <FormHorario key={h.id} inicial={editando} onCancelar={() => setEditando(null)} onGuardar={() => { setEditando(null); cargar(); }} />
                    ) : (
                        <section key={h.id} className={`${panel} p-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_14rem]`}>
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-4">
                                    <h2 className={`${display} text-xl font-semibold text-slate-900`}>{h.nombre}</h2>
                                    <span className="text-sm text-slate-500">
                                        {h.por_defecto ? 'Lo tiene todo el que no tenga otro asignado' : `${h.asignados} ${h.asignados === 1 ? 'persona' : 'personas'}`}
                                    </span>
                                </div>
                                <SemanaHorario jornada={h.jornada} />
                            </div>
                            <div className="flex flex-col gap-3 lg:border-l lg:border-slate-100 lg:pl-6">
                                <div>
                                    <p className={`${display} text-3xl font-bold text-sura-blue tabular-nums`}>{horas(horasSemana(h))}</p>
                                    <p className="text-xs text-slate-500">horas por semana</p>
                                </div>
                                <p className="text-sm text-slate-600">
                                    {h.tolerancia_min ? `${h.tolerancia_min} min de tolerancia` : 'Sin tolerancia: un minuto tarde ya es retardo'}
                                </p>
                                <p className="text-sm text-slate-600">
                                    {h.descuento_almuerzo_min ? `Descuenta ${h.descuento_almuerzo_min} min de almuerzo` : 'No descuenta almuerzo'}
                                </p>
                                <p className="text-sm text-slate-600">
                                    Llegar antes no suma. La hora extra cuenta desde {h.extra_minimo_min} min después de la salida y va aparte.
                                </p>
                                <button type="button" onClick={() => setEditando({ ...h })} className={`${botonClaro} self-start mt-auto`}>Editar</button>
                            </div>
                        </section>
                    )))}
                </div>
            )}

            {horarios && <Asignar horarios={horarios} onCambio={cargar} />}
        </div>
    );
};

export default HorariosVista;
